import { Sender } from 'xstate';
import { proStudioUrqlGql as urqlGql } from '@pypestream/api-services';

import { ProStudioContext, ProStudioEvents } from '../prostudio.xstate.types';
import { UserInterpreter } from '../user.xstate';
import { globalAppService } from '../app.xstate';
import { GLOBAL_APP_SUB_MACHINE_IDS } from '../app.xstate-utils';
import { checkForErrors, extractUserProjects, UserProject } from '../../utils';
import { reportObserverFailure } from '../report-observer-failure';

let hasUpdateUserInfoSubRun = false;

export const getUserData =
  (ctx: ProStudioContext, _event: ProStudioEvents) =>
  async (sendEvent: Sender<ProStudioEvents>) => {
    const userService = globalAppService.children.get(
      GLOBAL_APP_SUB_MACHINE_IDS.user
    ) as UserInterpreter;

    const userServiceSubscription = userService?.subscribe(async (state) => {
      const userContext = state?.context;

      const userId = userContext?.user?.id;

      if (!userId) return;

      if (hasUpdateUserInfoSubRun === false) {
        hasUpdateUserInfoSubRun = true;
        const defaultOrgId = userContext?.user?.defaultAccount?.defaultOrgId;
        const email = userContext?.user?.email;
        const status = userContext?.user?.status;
        const firstName = userContext?.user?.firstName;
        const lastName = userContext?.user?.lastName;

        try {
          const userProjectsResponse = await urqlGql.getUserProjects({
            userId,
          });

          checkForErrors(userProjectsResponse);

          const userProjects = extractUserProjects(
            userProjectsResponse.data?.tenancy_users[0]
          );

          const nextUserInfoState: ProStudioContext['userInfo'] & {
            userProjects: UserProject[];
          } = {
            ...ctx.userInfo,
            id: userId,
            defaultOrgId,
            email,
            userProjects,
            status,
            firstName,
            lastName,
          };

          sendEvent({
            type: 'updateUserInfo',
            data: nextUserInfoState,
          });
          // Released only HERE, after the projects have arrived. xstate v4
          // calls this listener at once when the user machine is already
          // running, so `userServiceSubscription` is still unassigned on that
          // first call and is read only after the `await` above. Unsubscribing
          // right after `subscribe` (as this used to) dropped the listener a
          // failure still needed for its retry.
          userServiceSubscription?.unsubscribe();
        } catch (error) {
          // The flag is lowered ONLY here, on failure. The retry the error
          // screen offers (`user.refetch`) reaches this callback as a NEW
          // emission of the user machine, and an emission is ignored while the
          // flag is up — so a failure that kept it raised would leave the page
          // on its loader for good, with the rejection unhandled (PSTUDIO-4492,
          // item 6). After a success the flag still holds, so later emissions do
          // not refetch the projects.
          hasUpdateUserInfoSubRun = false;
          console.error('Error fetching user projects:', error);
          reportObserverFailure(error, 'user-data-observer:user-projects');
          sendEvent({ type: 'setUserProjectsLoadFailed', failed: true });
        }
      }
    });
  };
